/**
 * Main Application Component
 *
 * This is the root component that orchestrates the entire UI.
 * It handles:
 * - Layout structure (panels arrangement)
 * - Menu event listeners (File > Open, Save, etc.)
 * - Keyboard shortcuts
 * - Syncing playback with project state
 *
 * REACT COMPONENT BASICS:
 * =======================
 * - Components are functions that return JSX (HTML-like syntax)
 * - Props are passed in as function arguments (we don't have any here)
 * - Hooks (useEffect, useState) add state and side effects to components
 * - Components re-render when their state or props change
 */

import { useEffect, useCallback, useState } from 'react';

// Components
import Layout from './components/Layout';
import TutorialModal from './components/TutorialModal/TutorialModal';

// Stores
import { useProjectStore } from '@/stores/projectStore';
import { usePlaybackStore } from '@/stores/playbackStore';

// Hooks
import { useAutoSave } from '@/hooks/useAutoSave';
import { useAudioPlayback } from '@/hooks/useAudioPlayback';

// Utils
import { loadProjectFromDB } from '@/utils/indexedDB';
import { imageStore } from '@/utils/browserAPI';
import { initializeTheme } from '@/stores/themeStore';
import { applyBeatColorScheme } from '@/styles/themes';
import { trackAppUsage } from '@/utils/analytics';
import { blobManager } from '@/utils/blobManager';

// Types
import { Project } from '@/types';

/**
 * App Component
 *
 * The top-level component that wraps everything else.
 */
const AUTO_SAVE_PROJECT_ID = 'autosave';

function App() {
  // ---------------------------------------------------------------------------
  // AUTO-SAVE & AUTO-RESTORE
  // ---------------------------------------------------------------------------

  // Track if we've loaded the auto-saved project
  const [hasLoadedAutoSave, setHasLoadedAutoSave] = useState(false);

  // Enable auto-save to IndexedDB
  useAutoSave();
  
  // Enable audio playback synchronization
  useAudioPlayback();
  
  // Unlock audio context on first user interaction (browser requirement)
  useEffect(() => {
    const unlockAudio = () => {
      // Create a silent audio context to unlock audio
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContext) {
        const ctx = new AudioContext();
        if (ctx.state === 'suspended') {
          ctx.resume().then(() => {
            console.log('[App] Audio context unlocked');
          });
        }
      }
      // Remove listeners after first interaction
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
    
    document.addEventListener('click', unlockAudio);
    document.addEventListener('keydown', unlockAudio);
    
    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // STORE ACCESS
  // ---------------------------------------------------------------------------

  // Project store actions
  const newProject = useProjectStore((state) => state.newProject);
  const loadProject = useProjectStore((state) => state.loadProject);
  const getProjectData = useProjectStore((state) => state.getProjectData);
  const setProjectPath = useProjectStore((state) => state.setProjectPath);
  const markSaved = useProjectStore((state) => state.markSaved);
  const projectPath = useProjectStore((state) => state.projectPath);
  const getTotalDuration = useProjectStore((state) => state.getTotalDuration);
  const nodes = useProjectStore((state) => state.nodes);
  const beatColorScheme = useProjectStore((state) => state.beatColorScheme);
  const showTutorial = useProjectStore((state) => state.showTutorial);
  const setShowTutorial = useProjectStore((state) => state.setShowTutorial);
  const closeTutorial = useProjectStore((state) => state.closeTutorial);
  const setTutorialPreference = useProjectStore((state) => state.setTutorialPreference);
  const clearTitleSelection = useProjectStore((state) => state.clearTitleSelection);
  const selectedTitleIds = useProjectStore((state) => state.selectedTitleIds);

  // Playback store actions
  const togglePlayback = usePlaybackStore((state) => state.togglePlayback);
  const playForward = usePlaybackStore((state) => state.playForward);
  const playReverse = usePlaybackStore((state) => state.playReverse);
  const stop = usePlaybackStore((state) => state.stop);
  const goToStart = usePlaybackStore((state) => state.goToStart);
  const goToEnd = usePlaybackStore((state) => state.goToEnd);
  const setTotalDuration = usePlaybackStore((state) => state.setTotalDuration);
  const cleanup = usePlaybackStore((state) => state.cleanup);

  // ---------------------------------------------------------------------------
  // THEME INITIALIZATION
  // ---------------------------------------------------------------------------

  /**
   * Initialize theme and beat colors on app start
   * This applies the saved theme from localStorage and beat color scheme
   */
  useEffect(() => {
    initializeTheme();
    applyBeatColorScheme(beatColorScheme);
  }, [beatColorScheme]);

  // ---------------------------------------------------------------------------
  // GLOBAL CLICK HANDLER - Deselect titles when clicking outside timeline
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      // Only handle if we have titles selected
      if (selectedTitleIds.length === 0) return;

      // Check if click is inside timeline
      const target = event.target as HTMLElement;
      const isInTimeline = target.closest('[data-timeline]') !== null;
      const isInProperties = target.closest('[data-properties]') !== null;

      // Deselect if clicking outside timeline and properties panel
      if (!isInTimeline && !isInProperties) {
        clearTitleSelection();
      }
    };

    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [selectedTitleIds, clearTitleSelection]);

  // ---------------------------------------------------------------------------
  // AUTO-RESTORE FROM INDEXEDDB
  // ---------------------------------------------------------------------------

  /**
   * Load the auto-saved project from IndexedDB on startup
   */
  useEffect(() => {
    if (hasLoadedAutoSave) return;

    const loadAutoSave = async () => {
      try {
        console.log('Checking for auto-saved project...');
        const savedProject = await loadProjectFromDB(AUTO_SAVE_PROJECT_ID);

        if (savedProject && savedProject.projectData) {
          console.log('Auto-saved project found, restoring...');

          // Images are already restored by loadProjectFromDB:
          // - Beat images (no colon in path) -> imageStore
          // - Reference images (with colon in path) -> blobManager
          // We just need to update imageStore with the returned images
          imageStore.clear();
          for (const [relativePath, blobUrl] of savedProject.images.entries()) {
            // Only add to imageStore if it's a legacy path (no colon)
            if (!relativePath.includes(':')) {
              imageStore.set(relativePath, blobUrl);
            }
            // Images with colons are already in blobManager from loadProjectFromDB
          }
          console.log('Restored', savedProject.images.size, 'images from auto-save');

          // Load project data with validation
          const data = savedProject.projectData as any;

          // Validate project data
          if (!data || typeof data !== 'object') {
            console.error('Auto-saved project has invalid structure, skipping restore');
            return;
          }

          // Validate nodes array
          if (!Array.isArray(data.nodes)) {
            console.warn('Auto-saved project nodes is not an array, using empty array');
            data.nodes = [];
          }

          // Validate titleNodes array
          if (!Array.isArray(data.titleNodes)) {
            console.warn('Auto-saved project titleNodes is not an array, using empty array');
            data.titleNodes = [];
          }

          // Validate referenceNodes array
          if (!Array.isArray(data.referenceNodes)) {
            console.warn('Auto-saved project referenceNodes is not an array, using empty array');
            data.referenceNodes = [];
          }

          // Validate noteNodes array
          if (!Array.isArray(data.noteNodes)) {
            data.noteNodes = [];
          }

          // Validate commentBoxes array
          if (!Array.isArray(data.commentBoxes)) {
            data.commentBoxes = [];
          }

          // Validate audioTracks array
          if (!Array.isArray(data.audioTracks)) {
            data.audioTracks = [];
          }

          // Validate sceneHeadingHistory array
          if (!Array.isArray(data.sceneHeadingHistory)) {
            data.sceneHeadingHistory = [];
          }

          // Validate tagHistory array
          if (!Array.isArray(data.tagHistory)) {
            data.tagHistory = [];
          }

          loadProject(
            {
              version: data.version || '1.0',
              name: data.name || 'Untitled Project',
              fps: data.fps || 24,
              aspectRatio: data.aspectRatio || '16:9',
              nodes: data.nodes,
              referenceNodes: data.referenceNodes,
              noteNodes: data.noteNodes,
              titleNodes: data.titleNodes,
              audioTracks: data.audioTracks,
              commentBoxes: data.commentBoxes,
              sceneHeadingHistory: data.sceneHeadingHistory,
              tagHistory: data.tagHistory,
              author: data.author,
              description: data.description,
              createdAt: data.createdAt || new Date().toISOString(),
              modifiedAt: data.modifiedAt || new Date().toISOString(),
            },
            '' // Empty path for web app
          );

          console.log('Auto-saved project restored successfully');
        } else {
          console.log('No auto-saved project found, creating new project with default node');
          newProject();
        }
      } catch (error) {
        console.error('Failed to restore auto-saved project:', error);
        // Don't alert here - auto-restore failures should be silent
        // User can still create a new project
      } finally {
        setHasLoadedAutoSave(true);
      }
    };

    loadAutoSave();
  }, [hasLoadedAutoSave, loadProject]);

  // ---------------------------------------------------------------------------
  // ANALYTICS TRACKING
  // ---------------------------------------------------------------------------

  /**
   * Track app usage once on startup
   * 
   * Only runs after the auto-save has been loaded to ensure we track
   * one session per app load. Tracks anonymously:
   * - Unique user ID (random UUID, no personal data)
   * - New vs returning user
   * - App version
   */
  useEffect(() => {
    // Wait until auto-save is loaded before tracking
    if (!hasLoadedAutoSave) return;

    // Track usage (silently fails if analytics unavailable)
    trackAppUsage();
  }, [hasLoadedAutoSave]);

  // ---------------------------------------------------------------------------
  // SYNC TOTAL DURATION
  // ---------------------------------------------------------------------------

  /**
   * Keep playback store in sync with project's total duration
   *
   * Whenever nodes change, recalculate the total duration and update
   * the playback store. This ensures the timeline knows how long the
   * project is.
   */
  useEffect(() => {
    const duration = getTotalDuration();
    setTotalDuration(duration);
  }, [nodes, getTotalDuration, setTotalDuration]);

  // ---------------------------------------------------------------------------
  // FILE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Save the current project to disk
   *
   * useCallback memoizes the function - it only creates a new function
   * reference when dependencies change. This prevents unnecessary re-renders
   * in child components that receive this as a prop.
   */
  const handleSaveProject = useCallback(
    async (savePath?: string) => {
      const pathToUse = savePath || projectPath;

      if (!pathToUse) {
        // No path - need to use Save As dialog
        const result = await window.electronAPI?.selectProjectDir();
        if (!result?.success || !result.filePath) return;
        return handleSaveProject(result.filePath);
      }

      // Get project data
      const projectData = getProjectData();

      // Collect all image paths that are actually used in the project
      const usedImagePaths = new Set<string>();

      // Add beat node images
      for (const node of nodes) {
        if (node.data.imagePath) {
          usedImagePaths.add(node.data.imagePath);
        }
      }

      // Add reference node images
      const referenceNodes = useProjectStore.getState().referenceNodes;
      for (const refNode of referenceNodes) {
        if (refNode.data.imagePath) {
          usedImagePaths.add(refNode.data.imagePath);
        }
      }

      console.log('[handleSaveProject] Used image paths:', Array.from(usedImagePaths));

      // Convert images to base64 for embedding in JSON (only used images)
      const images: Record<string, string> = {};

      // Save beat images from imageStore (only if used)
      for (const [relativePath, blobUrl] of imageStore.entries()) {
        if (!usedImagePaths.has(relativePath)) {
          console.log('[handleSaveProject] Skipping unused imageStore image:', relativePath);
          continue;
        }

        try {
          const response = await fetch(blobUrl);
          const blob = await response.blob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          images[relativePath] = base64;
          console.log('[handleSaveProject] Saved imageStore image:', relativePath);
        } catch (error) {
          console.error(`Failed to convert image ${relativePath}:`, error);
        }
      }

      // Save reference images from blobManager (only if used)
      for (const [key, blobUrl] of blobManager.getAll().entries()) {
        if (!usedImagePaths.has(key)) {
          console.log('[handleSaveProject] Skipping unused blobManager image:', key);
          continue;
        }

        try {
          const response = await fetch(blobUrl);
          const blob = await response.blob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          images[key] = base64;
          console.log('[handleSaveProject] Saved blobManager image:', key);
        } catch (error) {
          console.error(`Failed to convert blob-managed image ${key}:`, error);
        }
      }

      console.log('[handleSaveProject] Total images saved:', Object.keys(images).length);

      // Combine project data with images
      const projectWithImages = {
        ...projectData,
        images,
      };

      const jsonContent = JSON.stringify(projectWithImages, null, 2);

      // Write to file
      const result = await window.electronAPI?.writeFile(pathToUse, jsonContent);

      if (result?.success) {
        setProjectPath(pathToUse);
        markSaved();
        console.log('Project saved:', pathToUse);
      } else {
        console.error('Failed to save project:', result?.error);
      }
    },
    [projectPath, getProjectData, setProjectPath, markSaved]
  );

  /**
   * Open a project file from disk
   */
  const handleOpenProject = useCallback(
    async (filePath: string) => {
      const result = await window.electronAPI?.readFile(filePath);

      if (!result?.success || !result.content) {
        console.error('Failed to read project:', result?.error);
        alert(`Failed to read project file: ${result?.error || 'Unknown error'}`);
        return;
      }

      try {
        const fileData = JSON.parse(result.content) as any;

        // Validate project structure
        if (!fileData || typeof fileData !== 'object') {
          alert('Failed to load project: Invalid project structure.');
          return;
        }

        // Validate required fields
        if (!Array.isArray(fileData.nodes)) {
          alert('Failed to load project: Missing required fields. The file may be corrupted.');
          return;
        }

        // Convert base64 images to blob URLs
        const imageKeys = Object.keys(fileData.images || {});
        console.log('[handleOpenProject] Loading images from file. Total images:', imageKeys.length);
        console.log('[handleOpenProject] Image keys:', imageKeys);

        // Clear existing stores
        imageStore.clear();
        blobManager.revokeAll();
        console.log('[handleOpenProject] Cleared existing image stores');

        // Load images
        let beatImagesLoaded = 0;
        let referenceImagesLoaded = 0;
        for (const [key, base64] of Object.entries(fileData.images || {})) {
          try {
            const base64String = base64 as string;
            // Extract mime type and data from base64 string
            const match = base64String.match(/^data:([^;]+);base64,(.+)$/);
            if (!match) {
              console.warn(`Image ${key} has invalid base64 format`);
              continue;
            }

            const mimeType = match[1];
            const base64Data = match[2];

            // Convert base64 to blob
            const byteString = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const uint8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < byteString.length; i++) {
              uint8Array[i] = byteString.charCodeAt(i);
            }

            const blob = new Blob([arrayBuffer], { type: mimeType });

            // Determine storage location based on key format
            // - reference-image:* -> blobManager (reference nodes)
            // - node-image:* -> blobManager (beat nodes, new format)
            // - images/* -> imageStore (beat nodes, legacy format)
            if (key.startsWith('reference-image:') || key.startsWith('node-image:')) {
              // Store in blobManager
              const blobUrl = blobManager.create(blob, key);
              if (key.startsWith('reference-image:')) {
                referenceImagesLoaded++;
                console.log(`[handleOpenProject] Loaded reference image ${key} -> ${blobUrl}`);
              } else {
                beatImagesLoaded++;
                console.log(`[handleOpenProject] Loaded beat image (node-image) ${key} -> ${blobUrl}`);
              }
            } else {
              // Store in imageStore (legacy beat images with relative paths)
              const blobUrl = URL.createObjectURL(blob);
              imageStore.set(key, blobUrl);
              beatImagesLoaded++;
              console.log(`[handleOpenProject] Loaded beat image (legacy) ${key} -> ${blobUrl}`);
            }
          } catch (error) {
            console.error(`[handleOpenProject] Failed to convert image ${key}:`, error);
          }
        }

        console.log(`[handleOpenProject] Loading complete: ${beatImagesLoaded} beat images, ${referenceImagesLoaded} reference images`);
        console.log('[handleOpenProject] imageStore size:', imageStore.size);
        console.log('[handleOpenProject] blobManager count:', blobManager.getCount());

        // Load project data (without images field)
        const { images, ...projectData } = fileData;
        loadProject(projectData as Project, filePath);
        console.log('Project loaded:', filePath);
      } catch (error) {
        console.error('Failed to parse project file:', error);
        alert(`Failed to parse project file: ${(error as Error).message}. The file may be corrupted.`);
      }
    },
    [loadProject]
  );

  // ---------------------------------------------------------------------------
  // IMAGE CLEANUP UTILITIES
  // ---------------------------------------------------------------------------

  /**
   * Clean up unused images from stores
   * Removes images that aren't referenced by any node
   */
  const cleanupUnusedImages = useCallback(() => {
    console.log('[Cleanup] Starting cleanup of unused images');

    // Collect all image paths that are actually used
    const usedImagePaths = new Set<string>();

    // Add beat node images
    for (const node of nodes) {
      if (node.data.imagePath) {
        usedImagePaths.add(node.data.imagePath);
      }
    }

    // Add reference node images
    const referenceNodes = useProjectStore.getState().referenceNodes;
    for (const refNode of referenceNodes) {
      if (refNode.data.imagePath) {
        usedImagePaths.add(refNode.data.imagePath);
      }
    }

    console.log('[Cleanup] Used image paths:', Array.from(usedImagePaths));

    // Clean up imageStore
    let imageStoreRemoved = 0;
    for (const [key] of imageStore.entries()) {
      if (!usedImagePaths.has(key)) {
        console.log('[Cleanup] Removing unused imageStore image:', key);
        imageStore.delete(key);
        imageStoreRemoved++;
      }
    }

    // Clean up blobManager
    let blobManagerRemoved = 0;
    for (const [key] of blobManager.getAll().entries()) {
      if (!usedImagePaths.has(key)) {
        console.log('[Cleanup] Removing unused blobManager image:', key);
        blobManager.revoke(key);
        blobManagerRemoved++;
      }
    }

    console.log(`[Cleanup] Removed ${imageStoreRemoved} from imageStore, ${blobManagerRemoved} from blobManager`);
    console.log('[Cleanup] After cleanup - imageStore size:', imageStore.size, 'blobManager count:', blobManager.getCount());
  }, [nodes]);

  // Run cleanup on startup (after auto-save restore)
  // Only runs ONCE on app startup, with a longer delay to ensure all data is loaded
  useEffect(() => {
    if (hasLoadedAutoSave) {
      // Wait 5 seconds to ensure everything is fully loaded
      // This prevents cleanup from running while nodes are still being created/updated
      const cleanupTimer = setTimeout(() => {
        console.log('[App] Running one-time startup cleanup');
        cleanupUnusedImages();
      }, 5000);

      return () => clearTimeout(cleanupTimer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoadedAutoSave]); // Only depend on hasLoadedAutoSave to run once

  // ---------------------------------------------------------------------------
  // MENU EVENT LISTENERS
  // ---------------------------------------------------------------------------

  /**
   * Set up listeners for menu events from the main process
   *
   * The menu in Electron's main process sends events via IPC.
   * We listen for these events here and handle them appropriately.
   *
   * useEffect with an empty dependency array [] runs once on mount
   * and the returned cleanup function runs on unmount.
   */
  useEffect(() => {
    // Skip if not in Electron
    if (!window.electronAPI) {
      console.log('Running in browser mode (Electron API not available)');
      return;
    }

    // Set up menu event listeners
    const cleanupFns = [
      window.electronAPI.onMenuEvent('new-project', () => {
        // Clear image stores when creating a new project
        console.log('[Menu] New project - clearing image stores');
        imageStore.clear();
        blobManager.revokeAll();
        newProject();
      }),

      window.electronAPI.onMenuEvent('open-project', (filePath) => {
        if (filePath) handleOpenProject(filePath);
      }),

      window.electronAPI.onMenuEvent('save-project', () => {
        handleSaveProject();
      }),

      window.electronAPI.onMenuEvent('save-project-as', (filePath) => {
        if (filePath) handleSaveProject(filePath);
      }),

      window.electronAPI.onMenuEvent('toggle-playback', () => {
        togglePlayback();
      }),

      window.electronAPI.onMenuEvent('go-to-start', () => {
        goToStart();
      }),

      window.electronAPI.onMenuEvent('go-to-end', () => {
        goToEnd();
      }),
    ];

    // Cleanup all listeners on unmount
    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, [
    newProject,
    handleOpenProject,
    handleSaveProject,
    togglePlayback,
    goToStart,
    goToEnd,
  ]);

  // ---------------------------------------------------------------------------
  // KEYBOARD SHORTCUTS
  // ---------------------------------------------------------------------------

  /**
   * Global keyboard shortcut handler
   *
   * These work even when no specific element is focused.
   * Note: Some shortcuts are also defined in the Electron menu (main.ts)
   * with accelerators - those work even when the window isn't focused.
   */
  useEffect(() => {
    // Track which J/K/L keys are currently pressed and when they were pressed
    const keyPressTimestamps = new Map<string, number>();
    const HOLD_THRESHOLD = 250; // ms - if held longer than this, it's a hold action
    const TAP_THRESHOLD = 400; // ms - rapid taps within this time increase speed

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      switch (event.key) {
        case ' ':
          // Space = toggle playback
          event.preventDefault();
          togglePlayback();
          break;

        case 'Home':
          // Home = go to start
          event.preventDefault();
          goToStart();
          break;

        case 'End':
          // End = go to end
          event.preventDefault();
          goToEnd();
          break;
      }

      // J/K/L navigation
      // Always prevent default to stop Firefox quick find
      if (key === 'j' || key === 'k' || key === 'l') {
        event.preventDefault();
      }

      const now = performance.now();
      const lastPressTime = keyPressTimestamps.get(key);

      // Allow key press if:
      // 1. First press (not in map), OR
      // 2. Rapid tap (within TAP_THRESHOLD since last press - for speed increase), OR
      // 3. Was released and pressed again
      const shouldTrigger = !lastPressTime || (now - lastPressTime < TAP_THRESHOLD);

      // For K key, always trigger immediately (stop should be instant)
      if (key === 'k') {
        stop();
        keyPressTimestamps.set(key, now);
      }
      // For J/L, handle first press or rapid taps (for speed increase)
      else if ((key === 'j' || key === 'l') && shouldTrigger) {
        // Only set timestamp on truly first press to avoid browser repeat
        if (!lastPressTime || event.repeat === false) {
          keyPressTimestamps.set(key, now);

          switch (key) {
            case 'j':
              // J = play in reverse, or increase speed if already playing reverse
              playReverse();
              break;

            case 'l':
              // L = play forward, or increase speed if already playing forward
              playForward();
              break;
          }
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const pressTime = keyPressTimestamps.get(key);

      if (pressTime !== undefined) {
        const holdDuration = performance.now() - pressTime;
        keyPressTimestamps.delete(key);

        // For J and L keys, determine if it was a short press (toggle) or hold
        if (key === 'j' || key === 'l') {
          event.preventDefault();

          if (holdDuration >= HOLD_THRESHOLD) {
            // Long press (hold) - stop when released
            stop();
          }
          // Short press - playback continues (toggle behavior, already started on keydown)
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [togglePlayback, playForward, playReverse, stop, goToStart, goToEnd]);

  // ---------------------------------------------------------------------------
  // DRAG AND DROP FILE LOADING
  // ---------------------------------------------------------------------------

  /**
   * Handle drag and drop of .emberboard files
   */
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Show visual feedback that we can accept the drop
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleDrop = async (e: DragEvent) => {
      console.log('[Drag&Drop] Drop event triggered');
      e.preventDefault();
      e.stopPropagation();

      const files = e.dataTransfer?.files;
      console.log('[Drag&Drop] Files:', files?.length || 0);

      if (!files || files.length === 0) {
        console.log('[Drag&Drop] No files dropped');
        return;
      }

      const file = files[0];
      console.log('[Drag&Drop] File name:', file.name, 'Type:', file.type);

      // Check if it's a .emberboard file
      if (!file.name.endsWith('.emberboard') && !file.name.endsWith('.nodebeat') && file.type !== 'application/json') {
        alert('Please drop a .emberboard file');
        return;
      }

      try {
        console.log('Loading dropped file:', file.name);
        const text = await file.text();

        // Validate JSON parsing
        let projectData;
        try {
          projectData = JSON.parse(text);
        } catch (parseError) {
          console.error('JSON parse error:', parseError);
          alert('Failed to load project file: Invalid JSON format. The file may be corrupted.');
          return;
        }

        // Validate project structure
        if (!projectData || typeof projectData !== 'object') {
          alert('Failed to load project file: Invalid project structure.');
          return;
        }

        // Convert base64 images to blob URLs
        console.log('[Drag&Drop] Loading images from dropped file:', Object.keys(projectData.images || {}));

        // Clear existing stores
        imageStore.clear();
        blobManager.revokeAll();

        for (const [key, base64] of Object.entries(projectData.images || {})) {
          try {
            const base64String = base64 as string;
            // Extract mime type and data from base64 string
            const match = base64String.match(/^data:([^;]+);base64,(.+)$/);
            if (!match) {
              console.warn(`[Drag&Drop] Image ${key} has invalid base64 format`);
              continue;
            }

            const mimeType = match[1];
            const base64Data = match[2];

            // Convert base64 to blob
            const byteString = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const uint8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < byteString.length; i++) {
              uint8Array[i] = byteString.charCodeAt(i);
            }

            const blob = new Blob([arrayBuffer], { type: mimeType });

            // Determine storage location based on key format (same as handleOpenProject)
            if (key.startsWith('reference-image:') || key.startsWith('node-image:')) {
              // Store in blobManager
              const blobUrl = blobManager.create(blob, key);
              console.log(`[Drag&Drop] Loaded ${key.startsWith('reference-image:') ? 'reference' : 'beat'} image ${key} -> ${blobUrl}`);
            } else {
              // Store in imageStore (legacy beat images with relative paths)
              const blobUrl = URL.createObjectURL(blob);
              imageStore.set(key, blobUrl);
              console.log(`[Drag&Drop] Loaded beat image (legacy) ${key} -> ${blobUrl}`);
            }
          } catch (error) {
            console.error(`[Drag&Drop] Failed to convert image ${key}:`, error);
          }
        }

        console.log('[Drag&Drop] Total images loaded');
        console.log('[Drag&Drop] imageStore size:', imageStore.size);
        console.log('[Drag&Drop] blobManager count:', blobManager.getCount());

        // Load project into store
        loadProject(
          {
            version: projectData.version || '1.0',
            name: projectData.projectName || projectData.name || 'Untitled Project',
            fps: projectData.fps || 24,
            aspectRatio: projectData.aspectRatio || '16:9',
            nodes: projectData.nodes || [],
            referenceNodes: projectData.referenceNodes || [],
            noteNodes: projectData.noteNodes || [],
            commentBoxes: projectData.commentBoxes || [],
            sceneHeadingHistory: projectData.sceneHeadingHistory || [],
            createdAt: projectData.createdAt || new Date().toISOString(),
            modifiedAt: projectData.modifiedAt || new Date().toISOString(),
          },
          '' // Empty path for web app
        );

        console.log('Project loaded successfully from dropped file');
      } catch (error) {
        console.error('Failed to load dropped file:', error);
        alert('Failed to load project file. Please make sure it is a valid .emberboard file.');
      }
    };

    // Add event listeners to the entire window
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [loadProject]);

  // ---------------------------------------------------------------------------
  // CLEANUP ON UNMOUNT
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const handleCloseTutorial = () => {
    closeTutorial();
  };

  return (
    <div className="app">
      <Layout />
      <TutorialModal
        isVisible={showTutorial}
        onClose={handleCloseTutorial}
        onDismissPermanently={() => {
          // Only set the preference, don't close the modal
          // The user will need to click "Got it!" to close
          setTutorialPreference(true);
        }}
      />
    </div>
  );
}

export default App;
